import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import type { IWellnessTypePillProps } from '../../../interfaces';
import { daysRemaining, formatDaysLeft, getWellnessTypeStatus } from '../../functions';

/** Maps backend status strings to MUI Chip color variants. */
const STATUS_CHIP_COLOR: Record<string, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
  'Invite Sent': 'warning',
  'In Progress': 'info',
  'Completed': 'success',
  'Expired': 'error',
  'Superseded': 'default',
};

/** Renders a single assessment-type row with name, days-left badge, and status chip. */
export const WellnessTypePill = ({ item }: IWellnessTypePillProps) => {
  const { label } = getWellnessTypeStatus(item.status);
  const remaining = daysRemaining(item.expiresAt);
  const daysLeft = formatDaysLeft(remaining);
  const isActionable = item.status === 'Invite Sent' || item.status === 'In Progress';
  const chipColor = STATUS_CHIP_COLOR[item.status ?? ''] ?? 'default';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.100',
        px: 1.5,
        py: 1,
      }}
    >
      <Typography variant="body2" fontWeight={600} color="text.primary">
        {item.type}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isActionable && daysLeft && (
          <Typography
            variant="caption"
            fontWeight={500}
            sx={{ color: remaining !== null && remaining <= 1 ? 'error.main' : 'text.secondary' }}
          >
            {daysLeft}
          </Typography>
        )}
        <Chip label={label} size="small" color={chipColor} />
      </Box>
    </Box>
  );
};
